<template>
    <div :class="containerClass" ref="container" v-click-outside="clickOutside">
        <div :class="anchorClass" :style="computedAnchorStyle">
            <div ref="tooltip" class="ur-tooltip-wrapper" :style="computedTooltipStyle">
                <div v-if="!$scopedSlots.tooltip" :class="tooltipClass"><!--
                    $ver: v0.1.0
                    툴팁내 위치할 컨텐츠 영역입니다. 일반적으로 문자열이 위치합니다.
                    #The content area to place in the tooltip. Typically the string is located.
                 --><slot/>
                    <ur-icon v-show="closeIconShow" class="ur-tooltip__close" size="16px" @click="close">close</ur-icon>
                </div>
                <!--
                    $ver: v0.1.0
                    툴팁내 위치할 컨텐츠 영역입니다. tooltip으로 슬롯을 지정하여 사용할 수 있습니다. 일반적으로 문자열이 위치합니다.
                    #The content area to place in the tooltip. You can use the tooltip by specifying the slot. Typically the string is located.
                 -->
                <slot name="tooltip"/>
            </div>
        </div>
    </div>
</template>

<script>
import urEventBus from '../../../utils/eventBus/UrEventBus'
let idx = 0;
// ur-tooltip 구성 요소는 사용자가 요소를 가리킬 때 정보를 전달하는 데 유용합니다.
// #ur-tooltip component is useful for conveying information when a user hovers over an element.
export default {
    name: 'ur-tooltip',
    props: {
        // $ver: v0.1.0
        // 툴팁이 적용될 객체의 id를 지정합니다.
        // 아무것도 지정하지 않으면 previousElementSibling을 선택합니다.
        // #Specifies the id of the object to which the tooltip is applied.
        // #If nothing is specified, select previousElementSibling.
        targetId: {type: String},

        // $ver: v0.2.0
        // 툴팁의 모양입니다. ('label', 'info')
        // #The shape of the tooltip. ('label', 'info')
        shape: {type: String, default:'info'},

        // $ver: v0.1.0
        // 툴팁을 닫을 때 닫기버튼을 클릭하여 닫히도록 합니다. shape 속성이 'label'일 경우 무시됩니다.
        // #When closing the tooltip, click the Close button to close it. Ignored if shape attribute is 'label'.
        // $default: false
        manualClose: {type:Boolean, default: false},

        // $ver: v0.2.0
        // 툴팁이 표시될 방향입니다.
        // #The direction in which the tooltip is displayed.
        direction: {type:String, default: ''},

        // $ver: v0.1.0
        // 왼쪽에 툴팁을 표현합니다.
        // #Show tooltips on the left.
        // $default: false
        left: {type:Boolean, default: false},

        // $ver: v0.1.0
        // 오른쪽에 툴팁을 표현합니다.
        // #Show tooltips on the right.
        // $default: false
        right: {type:Boolean, default: false},

        // $ver: v0.1.0
        // 위쪽에 툴팁을 표현합니다.
        // #Show tooltips at the top.
        // $default: false
        top: {type:Boolean, default: false},

        // $ver: v0.1.0
        // 아래쪽에 툴팁을 표현합니다.
        // #Show tooltips at the bottom.
        // $default: false
        bottom: {type:Boolean, default: false},

        // $ver: v0.1.0
        // 마우스 오버 시 툴팁을 표현합니다.
        // #Show tooltip on mouseover.
        // $default: false
        mouseOver: {type:Boolean, default: false},

        // $ver: v0.1.0
        // mouseOver 속성의 약어입니다.
        // $abbr: mouseOver
        mover: {type:Boolean, default: false}, // alias of mouseOver

        // $ver: v0.1.0
        // mouseOver 속성의 약어입니다.
        // $abbr: mouseOver
        hover: {type:Boolean, default: false}, // alias of mouseOver

        // $ver: v0.1.2
        // scroll (overflow) 설정이 되어있는 컨테이너를 지정합니다. querySelector 방식이며 기본값은 .ur-scroll입니다.
        // #Specifies the container that has scroll (overflow) set on it. The querySelector method, which defaults to .ur-scroll.
        scrollContainer: {
            type: String,
            default: '.ur-scroll'
        },

        // $ver: v0.1.2
        // scroll이 발생 했을 때 target element와 함께 움직이는 컨텐츠 영역입니다.
        // #The content area that moves with the target element when scrolling occurs.
        contentContainer: {
            type: String,
            default: '.ur-scroll__content'
        }
    },
    data() {return {
        targetEl: {},
        showToggle: false,
        location: '',
        multiline: false,
        computedAnchorStyle: {top: '-10000px'},
        computedTooltipStyle: {},
    }},
    computed: {
        closeIconShow() {return (this.manualClose || this.multiline) && !this.$_hover && !this.$_label},
        $_hover() {
            return this.mouseOver || this.hover || this.mover;
        },
        $_label() {
            return this.shape === 'label';
        },
        containerClass() {
            return [
                'ur-tooltip-invisible-container',
                this.showToggle?'ur-tooltip-invisible-container--show':''
            ]
        },
        anchorClass() {
            return [
                'ur-tooltip-anchor',
                this.$_label? '' : (this.$scopedSlots.tooltip?'':'ur-tooltip-anchor__arrow--'+this.location)
            ]
        },
        tooltipClass() {
            return [
                'ur-tooltip',
                this.$_label? 'ur-tooltip-h24' : '',
                !this.$_label && (this.manualClose||this.multiline)?'ur-tooltip--with-close':'',
                this.multiline?'ur-tooltip--multiline':'',
            ]
        }
    },
    mounted() {
        // 1. find target element
        if(typeof this.targetId ==='string' && this.targetId[0] === '#') {
            this.targetEl= document.getElementById(this.targetId.slice(1));
        } else if(this.targetId) {
            this.targetEl = document.getElementById(this.targetId);
        } else {
            this.targetEl = this.$el.previousElementSibling
        }
        // 2. regist event
        if(this.$_hover) {
            // case.1 mouse-over 모드
            const elTagName = this.targetEl.tagName;
            this.targetEl.addEventListener('mouseenter',(e)=>{
                if(e.target.tagName !== elTagName) return;
                this.clickTrigger(e);
            },true); /* not MOUSE-OVER */
            this.targetEl.addEventListener('mouseleave',(e)=>{
                if(e.target.tagName !== elTagName) return;
                this.close(e);
            },true); /* not MOUSE-OUT */
            // 고민중 : 마우스를 올리면 켜지는데, 그상태에서 클릭하면 꺼짐.
            //          클릭으로 켜지진 않는데 꺼짐 기능을 구지 없앨 이유가 있을까?
        } else {
            // case.2 Click 모드
            this.targetEl.addEventListener('click',this.clickTrigger);
            // let me = this;
            // urEventBus.$on("close-tooltip", function(){
            //     // me.clickOutside();
            // });
        }

        if(this.$refs.tooltip.getBoundingClientRect().height> 32) {
            this.multiline = true;
        }
    },
    methods: {
        clickTrigger(e) {
            e.stopPropagation();
            if(this.showToggle===true) {
                this.clickOutside();
                return;
            }
            if(this.$refs.tooltip.children[0].clientWidth > 340){
                this.$refs.tooltip.children[0].style["width"] = '368px';
                this.$refs.tooltip.children[0].style["white-space"] = 'normal';
            }
            let contentContainer = this.targetEl.parentElement;
            let matchFuncName = contentContainer['matches']?'matches':'msMatchesSelector';
            if(!this.$el.parentElement[matchFuncName]('.ur-tooltip-outer-invisible-container')) {
                while(contentContainer && !(contentContainer[matchFuncName](this.contentContainer) || contentContainer.classList.contains('ur-screen'))) {
                    contentContainer = contentContainer.parentElement||undefined;
                }
                if (!contentContainer) {contentContainer = document.body;}
                let childNodes = contentContainer.children;
                let outerContainer;
                for(let node of childNodes) {
                    if(node.classList.contains('ur-tooltip-outer-invisible-container')) {
                        outerContainer = node;
                        break;
                    }
                }
                if(!outerContainer) {
                    outerContainer = document.createElement('div');
                    outerContainer.className ='ur-tooltip-outer-invisible-container';
                    contentContainer.appendChild(outerContainer);
                    outerContainer.appendChild(this.$el);
                } else {
                    outerContainer.appendChild(this.$el);
                }
            } else {
                contentContainer = this.$el.parentElement.parentElement;
            }
            let scrollContainer = contentContainer;
            while(scrollContainer && !(scrollContainer[matchFuncName](this.scrollContainer) || scrollContainer.classList.contains('ur-system'))) {
                scrollContainer = scrollContainer.parentElement || undefined;
            }
            if(!scrollContainer) {scrollContainer = document.body;}


            let positionLimit = scrollContainer.getBoundingClientRect();
            let target = this.targetEl.getBoundingClientRect();
            let container = this.$el.getBoundingClientRect();
            let tooltip = this.$refs.tooltip.getBoundingClientRect();
            let arrowWidth = !this.$scopedSlots.tooltip? 12:0;

            this.computedAnchorStyle = this.getAnchorStyle(positionLimit, target, container, tooltip,arrowWidth);
            this.computedTooltipStyle= this.getToolTipStyle(positionLimit, target, tooltip,arrowWidth);
            this.open();
        },
        getAnchorStyle(parent, target, container, tooltip,arrowWidth){
            let anchorStyle = {};
            let labeledOffset = this.$_label? 12:0;
            let verticalCenter = (target.top + target.bottom) / 2;
            let horizontalCenter = (target.left + target.right) / 2;
            //direction prop 에 더 우선순위를 둡니다.
            if(this.direction ==='left') {
                this.location = 'left';
                anchorStyle.left = ((target.left - 4) - container.left + labeledOffset) + 'px';
                anchorStyle.top = (verticalCenter - container.top + (this.$_label? 4:0)) + 'px';
                return anchorStyle;
            } else if(this.direction ==='right') {
                this.location = 'right';
                anchorStyle.left = ((target.right + 4) - container.left - labeledOffset) + 'px';
                anchorStyle.top = (verticalCenter - container.top) + 'px';
                return anchorStyle;
            } else if(this.direction ==='top') {
                this.location = 'top';
                anchorStyle.top = ((target.top - 4) - container.top + labeledOffset) + 'px';
                anchorStyle.left = (horizontalCenter - container.left) + 'px';
                return anchorStyle;
            } else if(this.direction ==='bottom') {
                this.location = 'bottom';
                anchorStyle.top = ((target.bottom + 4) - container.top - labeledOffset) + 'px';
                anchorStyle.left = (horizontalCenter - container.left) + 'px';
                return anchorStyle;
            }
            
            if(this.left) {
                this.location = 'left';
                anchorStyle.left = ((target.left - 4) - container.left + labeledOffset) + 'px';
                anchorStyle.top = (verticalCenter - container.top) + 'px';
            } else if(this.right) {
                this.location = 'right';
                anchorStyle.left = ((target.right + 4) - container.left - labeledOffset) + 'px';
                anchorStyle.top = (verticalCenter - container.top) + 'px';
            } else if(this.top) {
                this.location = 'top';
                anchorStyle.top = ((target.top - 4) - container.top + labeledOffset) + 'px';
                anchorStyle.left = (horizontalCenter - container.left) + 'px';
            } else if(this.bottom) {
                this.location = 'bottom';
                anchorStyle.top = ((target.bottom + 4) - container.top - labeledOffset) + 'px';
                anchorStyle.left = (horizontalCenter - container.left) + 'px';
            } else {
                if(target.right + 4 + arrowWidth + tooltip.width > parent.right){
                    if(target.bottom + 4 + arrowWidth + tooltip.height > parent.bottom) {
                        this.location='top';
                        anchorStyle.top = ((target.top - 4) - container.top + labeledOffset) + 'px';
                        anchorStyle.left = (horizontalCenter - container.left) + 'px';
                    } else {
                        this.location = 'bottom';
                        anchorStyle.top = ((target.bottom + 4) - container.top - labeledOffset) + 'px';
                        anchorStyle.left = (horizontalCenter - container.left) + 'px';
                    }
                } else {
                    this.location = 'right';
                    anchorStyle.left = ((target.right + 4) - container.left - labeledOffset) + 'px';
                    anchorStyle.top = (verticalCenter - container.top) + 'px';
                }
            }
            return anchorStyle;
        },
        getToolTipStyle(parent, target, tooltip,arrowWidth){
            let tooltipStyle = {};
            if(this.location === 'left') {
                tooltipStyle.right = (arrowWidth) + 'px';
                if((target.top + target.bottom) / 2 + tooltip.height - (this.$_label? 12 : 16) > parent.bottom) {
                    tooltipStyle.bottom = ((target.top + target.bottom) / 2 - parent.bottom) +'px';
                }else {
                    tooltipStyle.top = '-16px';
                }
            } else if(this.location === 'right') {
                tooltipStyle.left = (arrowWidth) + 'px';
                if((target.top + target.bottom) / 2 + tooltip.height - (this.$_label? 12 : 16) > parent.bottom) {
                    tooltipStyle.bottom = ((target.top + target.bottom) / 2 - parent.bottom) +'px';
                }else {
                    tooltipStyle.top = '-16px';
                }
            } else if(this.location === 'top') {
                tooltipStyle.bottom = (arrowWidth) + 'px';
                if((target.left + target.right) / 2 - tooltip.width / 2  < parent.left) {
                    tooltipStyle.left = (parent.left - (target.left + target.right) / 2) +'px';
                } else if((target.left + target.right) / 2 + tooltip.width / 2  > parent.right){
                    tooltipStyle.right = ((target.left + target.right) / 2 - parent.right + 20) +'px';
                }else {
                    tooltipStyle.left = (-tooltip.width/2)+'px';
                }
            } else {
                tooltipStyle.top = (arrowWidth) + 'px';
                if((target.left + target.right) / 2 - tooltip.width / 2  < parent.left) {
                    tooltipStyle.left = (parent.left - (target.left + target.right) / 2) +'px';
                } else if((target.left + target.right) / 2 + tooltip.width / 2  > parent.right){
                    tooltipStyle.right = ((target.left + target.right) / 2 - parent.right + 20) +'px';
                }else {
                    tooltipStyle.left = (-tooltip.width/2)+'px';
                }
            }
            return tooltipStyle;
        },
        clickOutside() {
            if(!this.manualClose || this.$_label) {
                this.close();
            }
        },
        open(){
            // $ver: v0.1.0
            // $arg: N/A
            // 툴팁이 펼쳐질 때, 다른 툴팁들을 닫는 이벤트를 발생시킵니다.
            // #When a tooltip is expanded, it raises an event that closes other tooltips.
            // urEventBus.$emit("close-tooltip");
            this.showToggle = true;
        },
        close(e) {
            this.computedAnchorStyle= {top: '-10000px'};
            this.showToggle = false;
        }
    },
    beforeDestroy() {
        if(this.targetEl) {
            this.targetEl.removeEventListener('mouseenter',this.clickTrigger);
            this.targetEl.removeEventListener('mouseout'  ,this.close);
        }
        if(this.$el.parentElement) {
            this.$el.parentElement.removeChild(this.$el);
        }
    }
}
</script>
